import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabaseApi, Category } from '../services/supabase';
import { 
  Tag, 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  Palette,
  Globe
} from 'lucide-react';
import styled from 'styled-components';

const CategoriesContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const Header = styled.header`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #667eea;
`;

const MainContent = styled.div`
  display: flex;
  min-height: calc(100vh - 70px);
`;

const Sidebar = styled.aside`
  width: 250px;
  background: white;
  border-right: 1px solid #e2e8f0;
  padding: 24px 0;
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  color: #64748b;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8fafc;
    color: #667eea;
  }
  
  &.active {
    background: #f1f5f9;
    color: #667eea;
    border-right: 3px solid #667eea;
  }
`;

const Content = styled.main`
  flex: 1;
  padding: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #1e293b;
`;

const AddButton = styled(Link)`
  background: #667eea;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
`;

const FiltersBar = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const SearchInput = styled.div`
  position: relative;
  max-width: 400px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
`;

const CategoryCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const CategoryIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color || '#667eea'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
`;

const CategoryActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
  
  &.delete:hover {
    background: #fef2f2;
    color: #dc2626;
  }
`;

const CategoryName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
`;

const CategorySlug = styled.p`
  color: #64748b;
  font-size: 14px;
  font-family: monospace;
  margin-bottom: 12px;
`;

const CategoryDescription = styled.p`
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
`;

const CategoryStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 14px;
  color: #64748b;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
`;

const EmptyDescription = styled.p`
  margin-bottom: 24px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

const Categories: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    filterCategories();
  }, [categories, searchTerm]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await supabaseApi.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterCategories = useCallback(() => {
    let filtered = categories;

    if (searchTerm) {
      filtered = filtered.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.slug.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCategories(filtered);
  }, [categories, searchTerm]);

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this category? This will also delete all associated projects.')) {
      try {
        const success = await supabaseApi.deleteCategory(id);
        if (success) {
          setCategories(categories.filter(c => c.id !== id));
        }
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  if (loading) {
    return (
      <CategoriesContainer>
        <Header>
          <HeaderContent>
            <Logo>🚀 Portfolio Admin</Logo>
          </HeaderContent>
        </Header>
        <MainContent>
          <Sidebar>
            <NavItem to="/">
              <Tag size={20} />
              Dashboard
            </NavItem>
            <NavItem to="/projects">
              <Tag size={20} />
              Projects
            </NavItem>
            <NavItem to="/categories" className="active">
              <Tag size={20} />
              Categories
            </NavItem>
          </Sidebar>
          <Content>
            <LoadingState>Loading categories...</LoadingState>
          </Content>
        </MainContent>
      </CategoriesContainer>
    );
  }

  return (
    <CategoriesContainer>
      <Header>
        <HeaderContent>
          <Logo>🚀 Portfolio Admin</Logo>
        </HeaderContent>
      </Header>

      <MainContent>
        <Sidebar>
          <NavItem to="/">
            <Tag size={20} />
            Dashboard
          </NavItem>
          <NavItem to="/projects">
            <Tag size={20} />
            Projects
          </NavItem>
          <NavItem to="/categories" className="active">
            <Tag size={20} />
            Categories
          </NavItem>
        </Sidebar>

        <Content>
          <PageHeader>
            <PageTitle>Categories</PageTitle>
            <AddButton to="/add-category">
              <Plus size={20} />
              Add Category
            </AddButton>
          </PageHeader>

          <FiltersBar>
            <SearchInput>
              <SearchIcon>
                <Search size={16} />
              </SearchIcon>
              <Input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchInput>
          </FiltersBar>

          {filteredCategories.length === 0 ? (
            <EmptyState>
              <EmptyIcon>🏷️</EmptyIcon>
              <EmptyTitle>No categories found</EmptyTitle>
              <EmptyDescription>
                {searchTerm 
                  ? 'Try adjusting your search' 
                  : 'Get started by adding your first category'
                }
              </EmptyDescription>
              {!searchTerm && (
                <AddButton to="/add-category">
                  <Plus size={20} />
                  Add Category
                </AddButton>
              )}
            </EmptyState>
          ) : (
            <CategoriesGrid>
              {filteredCategories.map((category) => (
                <CategoryCard key={category.id}>
                  <CategoryHeader>
                                         <CategoryIcon color={category.color || '#667eea'}>
                      {category.icon === 'Globe' ? <Globe size={24} /> : 
                       category.icon === 'Palette' ? <Palette size={24} /> : 
                       category.icon === 'Smartphone' ? '📱' :
                       category.icon === 'TrendingUp' ? '📈' :
                       category.icon === 'ShoppingCart' ? '🛒' :
                       category.icon === 'Brain' ? '🧠' :
                       category.icon === 'Cloud' ? '☁️' :
                       category.icon === 'Database' ? '🗄️' : '🏷️'}
                    </CategoryIcon>
                    <CategoryActions>
                      <ActionButton 
                        title="Edit"
                        onClick={() => navigate(`/edit-category/${category.id}`)}
                      >
                        <Edit size={16} />
                      </ActionButton>
                      <ActionButton 
                        title="Delete" 
                        className="delete"
                        onClick={() => handleDelete(category.id)}
                      >
                        <Trash2 size={16} />
                      </ActionButton>
                    </CategoryActions>
                  </CategoryHeader>
                  
                  <CategoryName>{category.name}</CategoryName>
                  <CategorySlug>/{category.slug}</CategorySlug>
                  <CategoryDescription>
                    {category.description || 'No description provided'}
                  </CategoryDescription>
                  
                  <CategoryStats>
                    <span>🎨 {category.color || 'No color'}</span>
                    <span>📅 {new Date(category.created_at).toLocaleDateString()}</span>
                  </CategoryStats>
                </CategoryCard>
              ))}
            </CategoriesGrid>
          )}
        </Content>
      </MainContent>
    </CategoriesContainer>
  );
};

export default Categories;
